import { AlertTriangle, ChevronDown } from "lucide-react"
import { toast } from "sonner"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { ShippingExcelSort } from "@/utils/shippingRowSort"

const LOAD_ERROR_TEXT = "순서 설정을 불러오지 못했습니다."

interface ShippingSortHeaderMenuProps {
  sort: ShippingExcelSort
  status: 'loading' | 'ready' | 'error'
  onChange: (value: ShippingExcelSort) => Promise<boolean>
}

/**
 * 배송엑셀 표 "순서" 헤더 = 정렬 기준 선택 드롭다운.
 * 스펙: docs/superpowers/specs/2026-10-06-shipping-excel-settlement-order-design.md §6
 * 헤더 폭이 약 36px라 settlement 라벨은 2줄, 에러 시 chevron 자리에 경고 아이콘.
 */
export function ShippingSortHeaderMenu({ sort, status, onChange }: ShippingSortHeaderMenuProps) {
  const isSettlement = status !== 'error' && sort === 'settlement'

  const handleValueChange = async (value: string) => {
    if (value !== 'entry' && value !== 'settlement') return
    if (value === sort) return
    const ok = await onChange(value)
    if (!ok) toast.error("순서 설정을 저장하지 못했습니다.")
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        disabled={status === 'loading'}
        title={status === 'error' ? LOAD_ERROR_TEXT : status === 'loading' ? "순서 설정을 불러오는 중입니다." : "순서 기준 선택"}
        aria-label={status === 'error' ? LOAD_ERROR_TEXT : "순서 기준 선택"}
        className={`w-full flex flex-col items-center justify-center rounded leading-tight outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-60 ${
          isSettlement ? "bg-white/25 py-0.5" : "hover:bg-white/15"
        }`}
      >
        {isSettlement ? (
          <span className="text-[11px] font-medium">
            <span className="block whitespace-nowrap">정산서</span>
            <span className="block whitespace-nowrap">순서</span>
          </span>
        ) : (
          <span className="whitespace-nowrap">순서</span>
        )}
        {status === 'error' ? (
          <AlertTriangle className="h-3 w-3 text-amber-300" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {status === 'error' && (
          <p className="px-2 py-1.5 text-xs text-amber-700">{LOAD_ERROR_TEXT}</p>
        )}
        <DropdownMenuRadioGroup value={isSettlement ? 'settlement' : 'entry'} onValueChange={handleValueChange}>
          <DropdownMenuRadioItem value="entry" disabled={status !== 'ready'}>
            <div className="flex flex-col">
              <span>입력 순서</span>
              <span className="text-xs text-gray-500">배송정보를 입력한 순서</span>
            </div>
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="settlement" disabled={status !== 'ready'}>
            <div className="flex flex-col">
              <span>정산서 순서</span>
              <span className="text-xs text-gray-500">정산서에 등록된 순서</span>
            </div>
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
